import axios from 'axios';
import { toast } from 'react-toastify';
import { apiURL } from './apiCalls';

/**
 * KYC Service Layer for handling all KYC-related API calls
 * Following the existing API pattern in the codebase
 */

// KYC API Endpoints
const KYC_ENDPOINTS = {
  PAN_VERIFY: '/api/v1/kyc/verify/pan',
  AADHAAR_VERIFY: '/api/v1/kyc/verify/aadhaar',
  BANK_VERIFY: '/api/v1/kyc/verify/bank-account',
  UPLOAD_DOCUMENT: '/api/v1/kyc/upload/document',
  GET_KYC_STATUS: '/api/v1/kyc/status',
  GET_KYC_HISTORY: '/api/v1/kyc/history',
  UPDATE_KYC_STATUS: '/api/v1/kyc/update-status',
  BULK_VERIFY: '/api/v1/kyc/bulk-verify',
  EXPORT_KYC_DATA: '/api/v1/kyc/export'
};

/**
 * Generic API call handler with error management
 */
const handleApiCall = async (apiCall) => {
  try {
    const response = await apiCall();
    return response;
  } catch (error) {
    console.error('KYC API Error:', error);
    const err = error;
    
    if (err.response) {
      const status = err.response.status;
      
      switch (status) {
        case 403:
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user_details');
          toast.error('Session expired. Please login again.');
          break;
        case 413:
          toast.error('File upload too large. Please try again.');
          break;
        case 422:
          toast.error(err.response.data?.message || 'Validation failed');
          break;
        case 429:
          toast.error('Too many requests. Please try again later.');
          break;
        case 500:
          toast.error('Server error. Please try again later.');
          break;
        default:
          toast.error(err.response.data?.message || 'An error occurred');
      }
    } else {
      toast.error('Network error. Please check your connection.');
    }
    
    return null;
  }
};

/**
 * KYC Service Class
 */
export class KYCService {
  
  /**
   * Verify PAN Card
   * @param {Object} panData - PAN verification data
   * @returns {Promise<Object|null>} API response or null
   */
  static async verifyPAN(panData,qr) {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'POST',
        url: apiURL + KYC_ENDPOINTS.PAN_VERIFY+'/'+qr,
        data: panData,
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      return response;
    });
  }

  /**
   * Verify Aadhaar Card
   * @param {Object} aadhaarData - Aadhaar verification data
   * @returns {Promise<Object|null>} API response or null
   */
  static async verifyAadhaar(aadhaarData,qr) {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'POST',
        url: apiURL + KYC_ENDPOINTS.AADHAAR_VERIFY+'/'+qr,
        data: aadhaarData,
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      return response;
    });
  }

  /**
   * Verify Bank Account
   * @param {Object} bankData - Bank verification data
   * @returns {Promise<Object|null>} API response or null
   */
  static async verifyBank(bankData,qr) {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'POST',
        url: apiURL + KYC_ENDPOINTS.BANK_VERIFY+'/'+qr,
        data: bankData,
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      return response;
    });
  }

  /**
   * Upload KYC Document
   * @param {Object} documentData - Document data with file
   * @returns {Promise<Object|null>} API response or null
   */
  static async uploadDocument(documentData) {
    return handleApiCall(async () => {
      const formData = new FormData();
      
      // Add document data
      Object.keys(documentData).forEach(key => {
        if (documentData[key] !== null && documentData[key] !== undefined) {
          formData.append(key, documentData[key]);
        }
      });

      const response = await axios({
        method: 'POST',
        url: apiURL + KYC_ENDPOINTS.UPLOAD_DOCUMENT,
        data: formData,
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    });
  }

  /**
   * Get KYC Status for user
   * @param {string} userId - User ID
   * @returns {Promise<Object|null>} API response or null
   */
  static async getKYCStatus(userId) {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'GET',
        url: apiURL + KYC_ENDPOINTS.GET_KYC_STATUS + '/' + userId,
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token')
        }
      });
      return response;
    });
  }

  /**
   * Get KYC History for user
   * @param {string} userId - User ID
   * @param {Object} filters - Optional filters
   * @returns {Promise<Object|null>} API response or null
   */
  static async getKYCHistory(userId, filters = {}) {
    return handleApiCall(async () => {
      const params = new URLSearchParams(filters);
      const response = await axios({
        method: 'GET',
        url: apiURL + KYC_ENDPOINTS.GET_KYC_HISTORY + '/' + userId + '?' + params.toString(),
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token')
        }
      });
      return response;
    });
  }

  /**
   * Update KYC Status (Admin only)
   * @param {string} kycId - KYC record ID
   * @param {Object} statusData - New status data
   * @returns {Promise<Object|null>} API response or null
   */
  static async updateKYCStatus(kycId, statusData) {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'PUT',
        url: apiURL + KYC_ENDPOINTS.UPDATE_KYC_STATUS + '/' + kycId,
        data: statusData,
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      return response;
    });
  }

  /**
   * Bulk verify KYC records (Admin only)
   * @param {Array} kycIds - Array of KYC record IDs
   * @param {string} action - Action to perform (approve/reject)
   * @returns {Promise<Object|null>} API response or null
   */
  static async bulkVerify(kycIds, action) {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'POST',
        url: apiURL + KYC_ENDPOINTS.BULK_VERIFY,
        data: { kycIds, action },
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      return response;
    });
  }

  /**
   * Export KYC Data (Admin only)
   * @param {Object} filters - Export filters
   * @param {string} format - Export format (csv/pdf)
   * @returns {Promise<Object|null>} API response or null
   */
  static async exportKYCData(filters = {}, format = 'csv') {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'POST',
        url: apiURL + KYC_ENDPOINTS.EXPORT_KYC_DATA,
        data: { filters, format },
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      });
      return response;
    });
  }

  /**
   * Get all KYC records with pagination and filters (Admin only)
   * @param {Object} params - Query parameters
   * @returns {Promise<Object|null>} API response or null
   */
  static async getAllKYCRecords(params = {}) {
    return handleApiCall(async () => {
      const queryParams = new URLSearchParams(params);
      const response = await axios({
        method: 'GET',
        url: apiURL + '/api/v1/kyc/records?' + queryParams.toString(),
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token')
        }
      });
      return response;
    });
  }

  /**
   * Get KYC statistics (Admin only)
   * @returns {Promise<Object|null>} API response or null
   */
  static async getKYCStatistics() {
    return handleApiCall(async () => {
      const response = await axios({
        method: 'GET',
        url: apiURL + '/api/v1/kyc/statistics',
        headers: { 
          'Authorization': 'Bearer ' + sessionStorage.getItem('token')
        }
      });
      return response;
    });
  }
}

/**
 * KYC Validation Schemas
 */
export const KYCValidationSchemas = {
  PAN: {
    pan_number: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
    name: /^[A-Za-z\s]+$/,
    date_of_birth: /^(0[1-9]|[12][0-9]|3[01])[\/\-](0[1-9]|1[012])[\/\-]\d{4}$/
  },
  AADHAAR: {
    aadhaar_number: /^[2-9]{1}[0-9]{3}[0-9]{4}[0-9]{4}$/,
    name: /^[A-Za-z\s]+$/
  },
  BANK: {
    account_number: /^[0-9]{9,18}$/,
    ifsc_code: /^[A-Z]{4}0[A-Z0-9]{6}$/,
    account_holder_name: /^[A-Za-z\s]+$/
  }
};

/**
 * KYC Status Constants
 */
export const KYC_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  VERIFIED: 'verified',
  REJECTED: 'rejected',
  EXPIRED: 'expired'
};

/**
 * KYC Document Types
 */
export const KYC_DOCUMENT_TYPES = {
  PAN_CARD: 'pan_card',
  AADHAAR_CARD: 'aadhaar_card',
  AADHAAR_FRONT: 'aadhaar_front',
  AADHAAR_BACK: 'aadhaar_back',
  BANK_PASSBOOK: 'bank_passbook',
  BANK_STATEMENT: 'bank_statement'
};

export default KYCService;
